import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ExternalLink, Sparkles } from 'lucide-react';
import { api } from '../../../../api/client';
import { toast } from '../../../../lib/toast';

// Shared list rendering for the three Copy-pattern modals (Schema/FAQ/Capsules).
// Caller provides:
//   - pillarCode: which breakdown[code] to sort by
//   - threshold: rows shown are those where breakdown[code] < threshold
//   - extract(html) → { count, scriptsText|html, types? } from optimizedContent lib
//   - copyLabel(extracted) → human-readable label for the toast / button count
//   - clipboardText(extracted) → the string to write to the clipboard
//   - instructions: array of strings/JSX for the "How to use" steps
export default function CopyFixList({
  domain,
  pillarCode,
  threshold,
  extract,
  copyLabel,
  clipboardText,
  instructions,
  emptyHint,
  onFixApplied,
}) {
  const qc = useQueryClient();

  const pagesQuery = useQuery({
    queryKey: ['pages', domain._id],
    queryFn: () => api.get(`/api/domains/${domain._id}/pages`).then((r) => r.data.pages),
  });

  if (pagesQuery.isPending) {
    return <div className="text-sm text-ink-400">Loading pages…</div>;
  }
  const allPages = pagesQuery.data || [];
  const worst = allPages
    .filter((p) => (p.audit_breakdown?.[pillarCode] ?? 0) < threshold)
    .sort((a, b) => (a.audit_breakdown?.[pillarCode] ?? 0) - (b.audit_breakdown?.[pillarCode] ?? 0))
    .slice(0, 5);

  if (worst.length === 0) {
    return (
      <div className="text-sm text-ink-500">
        No pages currently fail this check. {emptyHint}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-500">
        Showing the {worst.length} page{worst.length === 1 ? '' : 's'} most affected by this issue. Fix
        them and re-run the audit when you’re ready.
      </p>
      {worst.map((page) => (
        <CopyFixRow
          key={page._id}
          page={page}
          pillarScore={page.audit_breakdown?.[pillarCode] ?? 0}
          domain={domain}
          extract={extract}
          copyLabel={copyLabel}
          clipboardText={clipboardText}
          onFixApplied={onFixApplied}
          onRewriteSuccess={() => qc.invalidateQueries({ queryKey: ['pages', domain._id] })}
        />
      ))}

      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-emerald-800 hover:text-emerald-900 font-medium select-none">
          How to use the copied content
        </summary>
        <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-xs text-ink-600 bg-emerald-50/60 border border-emerald-100 rounded-lg p-3">
          {instructions.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </details>
    </div>
  );
}

function CopyFixRow({
  page,
  pillarScore,
  domain,
  extract,
  copyLabel,
  clipboardText,
  onFixApplied,
  onRewriteSuccess,
}) {
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detail, setDetail] = useState(null);

  // Lazy-load the page detail (we need optimized_content but the /pages list
  // intentionally strips it for payload size).
  const ensureDetail = async () => {
    if (detail) return detail;
    setLoadingDetail(true);
    try {
      const res = await api.get(`/api/domains/${domain._id}/pages/${page._id}`);
      setDetail(res.data.page);
      return res.data.page;
    } catch (err) {
      toast.error(err, 'Could not load page');
      return null;
    } finally {
      setLoadingDetail(false);
    }
  };

  const onCopy = async () => {
    const d = await ensureDetail();
    if (!d) return;
    const extracted = extract(d.optimized_content || '');
    if (!extracted.count) {
      toast.error(null, 'No content to copy — run "Rewrite with AI" on this page first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(clipboardText(extracted));
      toast.success(`Copied ${copyLabel(extracted)} to clipboard`);
      onFixApplied?.();
    } catch (err) {
      toast.error(err, 'Could not copy to clipboard');
    }
  };

  const rewriteMutation = useMutation({
    mutationFn: () =>
      api
        .post(`/api/domains/${domain._id}/pages/rewrite`, { pageUrl: page.url })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Page rewritten — extracted content is now available to copy');
      setDetail(null);
      onRewriteSuccess?.();
    },
    onError: (err) => toast.error(err, 'Rewrite failed'),
  });

  const hasOptimized = page.last_rewritten;

  let pathLabel = page.url;
  try {
    pathLabel = new URL(page.url).pathname || page.url;
  } catch {
    // leave pathLabel as-is
  }

  return (
    <div className="rounded-xl border border-gray-100 p-3 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="truncate font-medium text-ink-900">
              {page.title || pathLabel}
            </span>
            <a
              href={page.url}
              target="_blank"
              rel="noreferrer"
              className="text-ink-400 hover:text-brand-600 flex-none"
              title="Open page"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="text-xs text-ink-400 truncate">{page.url}</div>
        </div>
        <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md flex-none">
          {pillarScore} pts
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {hasOptimized ? (
          <button
            type="button"
            onClick={onCopy}
            disabled={loadingDetail}
            className="text-xs inline-flex items-center gap-1 text-emerald-800 hover:text-emerald-900 bg-emerald-100 hover:bg-emerald-200 px-2 py-1 rounded-md font-medium transition-colors disabled:opacity-50"
          >
            <Copy className="w-3 h-3" />
            {loadingDetail ? 'Loading…' : 'Copy'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => rewriteMutation.mutate()}
            disabled={rewriteMutation.isPending}
            className="text-xs inline-flex items-center gap-1 text-brand-800 hover:text-brand-900 bg-brand-100 hover:bg-brand-200 px-2 py-1 rounded-md font-medium transition-colors disabled:opacity-50"
          >
            <Sparkles className="w-3 h-3" />
            {rewriteMutation.isPending ? 'Rewriting…' : 'Rewrite with AI'}
          </button>
        )}
      </div>
    </div>
  );
}
